// --- React and Redux
import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
// --- Third-Party Libraries
import SortableTree from 'react-sortable-tree';
import 'react-sortable-tree/style.css';
import {debounce} from 'lodash';
// --- UI Components
import Typography from '@dtci/spellbook/Typography';
import Button from '@dtci/spellbook/Button';
import {ClearanceMemoTabs} from "../tabs";
// --- Dialogs
import {useSharedSectionDialog} from "../../dialogs/dialogs/sharedSections";
// --- Utilities and APIs
import {datastageApi} from "../../../api";
import {useLoading} from "../../../utils/hooks/useLoading";
import {expandAll, stripHtml, truncate} from '../utils';
import {useClearanceMemoTitlesActions} from "../../../store/clearanceMemoTitles/useClearanceMemoTitles";
// --- Styles
import './styles.scss';

/**
 * MemoForm Component
 *
 * Renders the memo form with a sidebar for navigation and a body for displaying memo details.
 * Includes functionality for creating new sections, adding shared sections, and moving nodes.
 *
 * @returns {JSX.Element} The rendered Memo Form component.
 */
export function MemoForm() {
    const {clearanceMemoTOC, title, product, isViewOnly} = useSelector((state) => state.titles);
    const {section} = useSelector((state) => state.sharedSections);
    const {isProductsModalOpen} = useSelector((state) => state.linkedProducts);
    const [treeData, setTreeData] = useState(clearanceMemoTOC);
    const [selectedNode, setSelectedNode] = useState();
    const {openSharedSectionDialog} = useSharedSectionDialog();
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {updateClearanceMemoTitles} = useClearanceMemoTitlesActions();
    const sectionRefs = useRef({});
    const [memoDetails, setMemoDetails] = useState(product);

    useEffect(() => {
        setTreeData(expandAll(clearanceMemoTOC.map(processSection).filter(Boolean)));
    }, [clearanceMemoTOC]);

    // Utility function to find a node in the tree
    const findNode = (nodes, sectionId) => {
        for (const node of nodes) {
            if (node?.clearanceMemoSection?.id === sectionId) {
                return node;
            }
            if (node.children) {
                const found = findNode(node.children, sectionId);
                if (found) return found;
            }
        }
        return null;
    };

    useEffect(() => {
        if (isProductsModalOpen && section) {
            const matchingNode = findNode(treeData, section?.id);
            if (matchingNode) {
                handleSingleClick(matchingNode);
            }
        }
    }, [section, treeData, isProductsModalOpen]);

    // Process each section of the memo
    const processSection = (section) => {
        if (!section?.clearanceMemoSection) return null;

        const {title, body} = section.clearanceMemoSection;

        // Create a new section object to avoid mutating the original state
        const newSection = {
            ...section,
            title: `${section.position} ${truncate(title || '', 25)}`,
            subtitle: truncate(stripHtml((body || '').replace(/\(+[xX][0-9]+.*?\)+/gi, '')), 25),
        };

        updateMemoDetails(newSection);

        if (Array.isArray(section.children)) {
            // Recursively process children and create a new array
            newSection.children = section.children.map(processSection).filter(Boolean);
        }

        return newSection;
    };

    // Update memo details based on the section
    const updateMemoDetails = (section) => {
        setMemoDetails((prev) => ({
            ...prev,
            lastRevisionDate: Math.max(prev.lastRevisionDate || 0, section.modifiedAt || 0),
            earliestRevisionDate: Math.min(
                prev.earliestRevisionDate || Infinity,
                section.createdAt || Infinity,
            ),
            firstPreparedBy:
                prev.earliestRevisionDate > section.createdAt
                    ? section.createdBy?.username || 'Unknown'
                    : prev.firstPreparedBy,
        }));
    };

    // Handle creating a new section
    const handleCreateSection = async (isShared) => {
        setLoadingTrue();
        try {
            await datastageApi.createSection(title, isShared, selectedNode);
            await refreshTOC();
        } catch (error) {
            console.error('Error creating section:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Handle opening the shared section modal
    const onOpenSharedSectionModal = async () => {
        setLoadingTrue();
        try {
            const res = await datastageApi.sharedSections({}, [0, 24], ['id', 'DESC']);
            const sharedSections = res.content.map((x) => ({label: x?.title || '', value: x.id}));
            setLoadingFalse();

            // Open the shared section dialog
            openSharedSectionDialog(sharedSections, title, async ({section}) => {
                setLoadingTrue();
                try {
                    await datastageApi.addSharedSection(section, title, selectedNode);
                    await refreshTOC();
                } catch (error) {
                    console.error('Error adding shared section:', error);
                } finally {
                    setLoadingFalse();
                }
            });
        } catch (error) {
            console.error('Error loading shared sections:', error);
            setLoadingFalse();
        }
    };

    // Refresh the table of contents
    const refreshTOC = async () => {
        const toc = await datastageApi.clearanceMemoTOC(title.id);
        setTreeData(expandAll(toc.map(processSection).filter(Boolean)));
    };

    // Handles the properties of each node in the tree
    const getNodeProps = (node) => {
        try {
            return {
                onClick: () => handleSingleClick(node),
                onDoubleClick: () => handleDoubleClick(node),
                className: selectedNode?.id === node?.id ? 'selected-node' : '',
            };
        } catch (error) {
            console.error('Error in getNodeProps:', error);
            return {};
        }
    };

    // Handle node click and scroll to the section
    const handleSingleClick = debounce((node) => {
        setSelectedNode(node);
        sectionRefs.current[node?.clearanceMemoSection?.id]?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, 300); // 300ms debounce delay

    // Handle node double click to edit the section
    const handleDoubleClick = (node) => {

        if (isViewOnly || !node.clearanceMemoSection) return; // Prevent double-click if ViewOnly
        setSelectedNode(node);

        updateClearanceMemoTitles({
            isSectionModalOpen: true,
            section: {
                ...node.clearanceMemoSection,
                id: node.id,
                clearanceMemoSectionId: node?.clearanceMemoSectionId,
                isVerified: node?.isPositionConfirmed || false,
                isTitleHidden: node?.isTitleHidden || false,
                isShared: node?.clearanceMemoSection?.isShareable || false,
            },
        });
    };

    // Handle moving a node in the tree
    const handleMoveNode = async ({node, nextParentNode, nextTreeIndex, prevTreeIndex}) => {
        setLoadingTrue();
        try {
            await datastageApi.updateClearanceMemoTOC(node.id, {
                newParentNodeId: nextParentNode?.id || null,
                nextTreeIndex,
                prevTreeIndex,
            });
            await refreshTOC();
        } catch (error) {
            console.error('Error moving node:', error);
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <div className="memo-layout">
            {/* Sidebar for memo navigation */}
            <div className="memo-sidebar">
                <Typography variant="T40">Memo Table of Contents</Typography>
                {!isViewOnly && (
                    <div className="memo-buttons">
                        {[
                            {label: 'New Section', action: () => handleCreateSection(false)},
                            {
                                label: 'New Shared Section',
                                action: () => handleCreateSection(true),
                            },
                            {label: 'Add Shared Section', action: onOpenSharedSectionModal},
                        ].map(({label, action}) => (
                            <Button
                                key={label}
                                iconType="fa-regular fa-plus"
                                type="button"
                                styleType="link"
                                onClick={action}
                            >
                                {label}
                            </Button>
                        ))}
                    </div>
                )}
                {/* Tree view for memo sections */}
                <div className="memo-tree-scroll">
                    <SortableTree
                        treeData={treeData}
                        onChange={setTreeData}
                        isVirtualized={false}
                        onMoveNode={handleMoveNode}
                        canDrag={!isViewOnly}
                        generateNodeProps={({ node }) => ({
                            ...getNodeProps(node),
                            buttons: isViewOnly
                                ? [] // Hide buttons when isViewOnly is true
                                : [
                                    !node?.clearanceMemoSection?.isPublic && (
                                        <Button
                                            key="private"
                                            iconType="fa-solid fa-eye-slash"
                                            sizeType="small"
                                            styleType="link"
                                            className="private-button"
                                            disabled
                                        />
                                    ),
                                    node?.clearanceMemoSection?.isShareable && (
                                        <Button
                                            key="link"
                                            iconType="fa-solid fa-link"
                                            sizeType="small"
                                            styleType="link"
                                            className="link-button"
                                            disabled
                                        />
                                    ),

                                    <Button
                                        key="edit"
                                        iconType="fa-regular fa-pen-to-square"
                                        sizeType="small"
                                        styleType="link"
                                        className="edit-button"
                                        onClick={() => handleDoubleClick(node)}
                                    />,
                                ].filter(Boolean),
                        })}
                    />
                </div>
            </div>
            {/* Body for displaying memo details */}
            <div className="memo-body">
                <ClearanceMemoTabs
                    treeData={treeData}
                    title={title}
                    sectionRefs={sectionRefs}
                    memoDetails={memoDetails}
                />
            </div>
        </div>
    );
}
